import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { FileText, Download, Eye, Calendar, User, Mail, Phone, ArrowLeft, Trash2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const AdminNewHires = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const formFilter = searchParams.get('form');
  
  const [loading, setLoading] = useState(true);
  const [idUploads, setIdUploads] = useState([]);
  const [filteredUploads, setFilteredUploads] = useState([]);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingWorker, setDeletingWorker] = useState(false);

  useEffect(() => {
    loadIdUploads();
  }, []);
  
  useEffect(() => {
    applyFormFilter();
  }, [idUploads, formFilter]);

  const applyFormFilter = () => {
    if (!formFilter) {
      setFilteredUploads(idUploads);
      return;
    }
    
    // Filter to show only workers who have submissions for the specified form
    const filtered = idUploads.map(userGroup => {
      const filteredDocs = userGroup.uploads.filter(upload => {
        // Check if this is a PDF form submission matching the filter
        return upload.documentType === 'pdf_form' && upload.type === formFilter;
      });
      
      if (filteredDocs.length > 0) {
        return {
          ...userGroup,
          uploads: filteredDocs
        };
      }
      return null;
    }).filter(Boolean);
    
    setFilteredUploads(filtered);
  };
  
  const clearFilter = () => {
    setSearchParams({});
  };

  const loadIdUploads = async () => {
    setLoading(true);
    try {
      // Load ID uploads (Driver's License, Passport, etc.)
      const idResponse = await axiosInstance.get('/admin/id-uploads');
      
      // Load PDF form submissions (W-4, I-9, W-9, etc.)
      const pdfResponse = await axiosInstance.get('/admin/pdf-form-submissions');
      
      // Load onboarding submissions (Personal Info, Emergency Contact, etc.)
      const onboardingResponse = await axiosInstance.get('/admin/onboarding-submissions');
      
      // Load custom form submissions (Ohio W4, MI-W4, etc.)
      let customFormsResponse = { data: [] };
      try {
        customFormsResponse = await axiosInstance.get('/admin/form-submissions');
      } catch (e) {
        console.log('No custom form submissions endpoint');
      }
      
      // Fetch all users to get names (more efficient than individual requests)
      const usersResponse = await axiosInstance.get('/admin/users');
      const userMap = {};
      usersResponse.data.forEach(user => {
        userMap[user.id] = user.name || user.email || 'Unknown';
      });
      
      // Group by user
      const groupedByUser = {};
      
      // Track seen submission IDs to prevent duplicates
      const seenSubmissionIds = new Set();
      
      // Add ID uploads
      idResponse.data.forEach(upload => {
        if (!groupedByUser[upload.userId]) {
          groupedByUser[upload.userId] = {
            userId: upload.userId,
            userName: userMap[upload.userId] || upload.userName || 'Unknown',
            userEmail: upload.userEmail || '',
            uploads: []
          };
        }
        groupedByUser[upload.userId].uploads.push({
          ...upload,
          documentType: 'id_upload'
        });
      });
      
      // Add custom form submissions (from Smart Form Builder)
      // Skip if already seen (prevents duplicates with pdf_form_submissions)
      customFormsResponse.data.forEach(submission => {
        if (seenSubmissionIds.has(submission.id)) return;
        seenSubmissionIds.add(submission.id);
        
        const userId = submission.workerId;
        if (!groupedByUser[userId]) {
          groupedByUser[userId] = {
            userId: userId,
            userName: userMap[userId] || submission.workerName || 'Unknown',
            userEmail: '',
            uploads: []
          };
        }
        const formName = submission.formName || submission.formTitle || 'Unknown Form';
        const workerName = submission.workerName || submission.submitterName || 'Unknown';
        groupedByUser[userId].uploads.push({
          id: submission.id,
          type: formName,
          fileName: `${formName.replace(/\s+/g, '_')}_${workerName.replace(/\s+/g, '_')}.pdf`,
          uploadedAt: submission.submittedAt,
          userName: workerName,
          filledPdfUrl: submission.filledPdfUrl || submission.filledPdfPath,
          documentType: 'custom_form',
          data: submission.data
        });
      });
      
      // Add PDF form submissions
      // Skip if already seen (prevents duplicates with custom form submissions)
      pdfResponse.data.forEach(submission => {
        if (seenSubmissionIds.has(submission.id)) return;
        seenSubmissionIds.add(submission.id);
        
        const userId = submission.submittedBy;
        if (!groupedByUser[userId]) {
          groupedByUser[userId] = {
            userId: userId,
            userName: userMap[userId] || submission.submitterName || 'Unknown',
            userEmail: submission.submitterEmail || '',
            uploads: []
          };
        }
        const pdfFormTitle = submission.formTitle || submission.formName || 'Unknown Form';
        const pdfSubmitterName = submission.submitterName || submission.workerName || 'Unknown';
        groupedByUser[userId].uploads.push({
          id: submission.id,
          pdfFormId: submission.pdfFormId || submission.formId,
          type: pdfFormTitle,
          fileName: `${pdfFormTitle.replace(/\s+/g, '_')}_${pdfSubmitterName.replace(/\s+/g, '_')}.pdf`,
          uploadedAt: submission.submittedAt,
          userName: pdfSubmitterName,
          userEmail: submission.submitterEmail || '',
          filledPdfPath: submission.filledPdfPath || submission.filledPdfUrl,
          documentType: 'pdf_form'
        });
      });
      
      // Add onboarding submissions
      onboardingResponse.data.forEach(submission => {
        const userId = submission.userId;
        if (!groupedByUser[userId]) {
          groupedByUser[userId] = {
            userId: userId,
            userName: userMap[userId] || submission.userName || 'Unknown',
            userEmail: submission.userEmail || '',
            uploads: []
          };
        }
        
        // Check if this is a new onboarding_section or old onboarding format
        if (submission.documentType === 'onboarding_section') {
          // New format - individual sections (Personal Info, Direct Deposit, etc.)
          groupedByUser[userId].uploads.push({
            id: submission.id,
            type: submission.sectionType || 'Onboarding Section',
            fileName: `${submission.sectionType || 'Document'}.pdf`,
            uploadedAt: submission.submittedAt,
            userName: submission.userName,
            userEmail: submission.userEmail,
            documentType: 'onboarding',  // Use 'onboarding' so download works
            data: submission.data
          });
        } else {
          // Old format - complete onboarding form
          groupedByUser[userId].uploads.push({
            id: submission.id,
            type: 'Complete Onboarding Form',
            fileName: 'Onboarding_Form.pdf',
            uploadedAt: submission.submittedAt,
            userName: submission.userName,
            userEmail: submission.userEmail,
            documentType: 'onboarding',
            data: submission.data
          });
        }
      });
      
      // Add combined PDFs for each worker
      Object.keys(groupedByUser).forEach(userId => {
        const worker = groupedByUser[userId];
        const hasOnboarding = worker.uploads.some(u => u.documentType === 'onboarding');
        const hasTaxForms = worker.uploads.some(u => u.documentType === 'pdf_form');
        const hasIdUploads = worker.uploads.some(u => u.documentType === 'id_upload');
        
        // Add "Personal Information" PDF (ONLY if onboarding data exists)
        const onboardingDoc = worker.uploads.find(u => u.documentType === 'onboarding');
        if (hasOnboarding && onboardingDoc && onboardingDoc.id) {
          worker.uploads.unshift({
            userId: userId,
            onboardingId: onboardingDoc.id, // Store the actual onboarding ID
            type: `${worker.userName} - Personal Information`,
            fileName: `${worker.userName} - Personal Information.pdf`,
            uploadedAt: new Date().toISOString(),
            userName: worker.userName,
            documentType: 'personal_info_pdf',
            isPriority: true
          });
        }
        
        // Add "Tax Forms" PDF (if tax forms or ID uploads exist)
        if (hasTaxForms || hasIdUploads) {
          worker.uploads.unshift({
            userId: userId,
            type: `${worker.userName} - Tax Forms`,
            fileName: `${worker.userName} - Tax Forms.pdf`,
            uploadedAt: new Date().toISOString(),
            userName: worker.userName,
            documentType: 'tax_forms_pdf',
            isPriority: true
          });
        }
      });
      
      setIdUploads(Object.values(groupedByUser));
    } catch (error) {
      console.error('Failed to load new hire documents:', error);
      toast.error('Failed to load new hire documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      // Get token explicitly for blob downloads (failsafe for Preview environment)
      const token = localStorage.getItem('token');
      const response = await axiosInstance.get('/admin/id-uploads/download-all', {
        responseType: 'blob',
        headers: token ? {
          'Authorization': `Bearer ${token}`
        } : {}
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `new-hire-documents-${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('All documents downloaded successfully');
    } catch (error) {
      console.error('Failed to download all documents:', error);
      toast.error('Failed to download documents');
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleViewUpload = async (upload) => {
    setSelectedUpload(upload);
    setShowViewDialog(true);
  };

  const handleDownloadSingle = async (upload) => {
    try {
      let endpoint;
      if (upload.documentType === 'custom_form') {
        // Custom form submissions - use download endpoint (will generate on-demand if needed)
        endpoint = `/pdf-form-submissions/${upload.id}/download`;
      } else if (upload.documentType === 'personal_info_pdf') {
        // Combined Personal Information PDF - use onboarding ID
        endpoint = `/admin/onboarding-pdf/${upload.onboardingId || upload.userId}`;
      } else if (upload.documentType === 'tax_forms_pdf') {
        // Combined Tax Forms PDF (ID pictures + tax forms)
        endpoint = `/admin/tax-forms-pdf/${upload.userId}`;
      } else if (upload.documentType === 'pdf_form') {
        // PDF form submissions - always use the download endpoint
        // It will try filledPdfPath first, then generate on-demand if needed
        endpoint = `/pdf-form-submissions/${upload.id}/download`;
      } else if (upload.documentType === 'onboarding') {
        // Onboarding submissions
        endpoint = `/admin/onboarding-pdf/${upload.id}`;
      } else {
        // ID uploads
        endpoint = `/id-uploads/${upload.id}/view`;
      }
      
      // Get token explicitly for blob downloads (failsafe for Preview environment)
      const token = localStorage.getItem('token');
      const response = await axiosInstance.get(endpoint, {
        responseType: 'blob',
        headers: token ? {
          'Authorization': `Bearer ${token}`
        } : {}
      });
      
      // Get the actual file extension from the original filename
      const fileExtension = upload.fileName?.split('.').pop() || 'pdf';
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      const uploadId = upload.id || upload.onboardingId || 'document';
      const shortId = typeof uploadId === 'string' ? uploadId.slice(0, 8) : 'doc';
      const fileName = `${upload.type?.replace(/[^a-zA-Z0-9]/g, '-') || 'document'}-${upload.userName?.replace(/\s+/g, '-')}-${shortId}.${fileExtension}`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Document downloaded');
    } catch (error) {
      console.error('Failed to download document:', error);
      console.error('Error details:', error.response);
      toast.error(error.response?.data?.detail || 'Failed to download document');
    }
  };

  const handleDeleteDocument = async (upload) => {
    if (!window.confirm(`Delete ${upload.type} for ${upload.userName}? This cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      // Use different endpoint based on document type
      let deleteEndpoint;
      if (upload.documentType === 'custom_form') {
        deleteEndpoint = `/admin/form-submissions/${upload.id}`;
      } else if (upload.documentType === 'pdf_form') {
        deleteEndpoint = `/pdf-form-submissions/${upload.id}`;
      } else if (upload.documentType === 'onboarding' || upload.documentType === 'onboarding_section') {
        deleteEndpoint = `/admin/onboarding-submissions/${upload.id}`;
      } else if (upload.documentType === 'id_upload') {
        deleteEndpoint = `/id-uploads/${upload.id}`;
      } else {
        // Default to id-uploads for backwards compatibility
        deleteEndpoint = `/id-uploads/${upload.id}`;
      }
      
      await axiosInstance.delete(deleteEndpoint);
      toast.success('Document deleted successfully');
      
      // Reload the list
      await loadIdUploads();
      
      // Close dialog if viewing deleted upload
      if (selectedUpload?.uploads?.some(u => u.id === upload.id)) {
        setShowViewDialog(false);
      }
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteWorkerAndFiles = async (workerData) => {
    const workerName = workerData.userName || 'this worker';
    const fileCount = workerData.uploads?.length || 0;
    
    if (!window.confirm(
      `⚠️ DELETE WORKER & ALL FILES?\n\n` +
      `Worker: ${workerName}\n` +
      `Files to delete: ${fileCount}\n\n` +
      `This will permanently delete:\n` +
      `• All ID uploads\n` +
      `• All tax forms\n` +
      `• All onboarding data\n` +
      `• All certificates\n` +
      `• All training progress\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Type the worker's name to confirm deletion.`
    )) {
      return;
    }
    
    // Second confirmation by typing name
    const confirmation = window.prompt(
      `To confirm deletion, type the worker's full name:\n"${workerName}"`
    );
    
    if (confirmation !== workerName) {
      toast.error('Name did not match. Deletion cancelled.');
      return;
    }

    setDeletingWorker(true);
    try {
      const response = await axiosInstance.delete(`/admin/worker/${workerData.userId}/delete-all`);
      
      toast.success(response.data.message || 'Worker and all files deleted successfully');
      
      // Close dialog and reload list
      setShowViewDialog(false);
      await loadIdUploads();
      
    } catch (error) {
      console.error('Failed to delete worker and files:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete worker data');
    } finally {
      setDeletingWorker(false);
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const totalUploads = idUploads.reduce((sum, user) => sum + user.uploads.length, 0);
  const displayUploads = filteredUploads;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading new hire documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate(-1)}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">New Hire Documents</h1>
              <p className="text-gray-600 mt-1">
                Review and download worker onboarding documents
              </p>
              {formFilter && (
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="text-sm">
                    Filtered by: {formFilter}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilter}
                    className="h-6 px-2 text-xs"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Clear Filter
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={handleDownloadAll}
              disabled={downloadingAll || totalUploads === 0}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              {downloadingAll ? 'Downloading...' : 'Download All as ZIP'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                {formFilter ? 'Filtered Workers' : 'Total Workers'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{displayUploads.length}</div>
              {formFilter && (
                <p className="text-xs text-gray-500 mt-1">
                  ({idUploads.length} total)
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                {formFilter ? 'Filtered Documents' : 'Total Documents'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {displayUploads.reduce((sum, user) => sum + user.uploads.length, 0)}
              </div>
              {formFilter && (
                <p className="text-xs text-gray-500 mt-1">
                  ({totalUploads} total)
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Recent Submissions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {displayUploads.filter(u => {
                  const latestUpload = u.uploads[u.uploads.length - 1];
                  const uploadDate = new Date(latestUpload.uploadedAt);
                  const daysSince = (Date.now() - uploadDate.getTime()) / (1000 * 60 * 60 * 24);
                  return daysSince <= 7;
                }).length}
              </div>
              <p className="text-xs text-gray-500 mt-1">Last 7 days</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {displayUploads.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg mb-2">
                {formFilter ? `No ${formFilter} submissions found` : 'No new hire documents yet'}
              </p>
              <p className="text-gray-500 text-sm">
                {formFilter 
                  ? 'Try clearing the filter to see all documents'
                  : 'Documents will appear here when workers complete their onboarding and upload their forms'
                }
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              Worker Documents
              {formFilter && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  (Showing {formFilter} submissions only)
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Click on a document to view details or download
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Worker</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Documents</TableHead>
                  <TableHead>Latest Upload</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayUploads.map((userGroup) => {
                  const latestUpload = userGroup.uploads[userGroup.uploads.length - 1];
                  return (
                    <TableRow key={userGroup.userId}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{userGroup.userName}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Mail className="w-4 h-4" />
                          {userGroup.userEmail}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="gap-1">
                          <FileText className="w-3 h-3" />
                          {userGroup.uploads.length} document{userGroup.uploads.length !== 1 ? 's' : ''}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          {formatDate(latestUpload.uploadedAt)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedUpload(userGroup);
                              setShowViewDialog(true);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Documents for {selectedUpload?.userName}</DialogTitle>
            <DialogDescription>
              {selectedUpload?.userEmail}
            </DialogDescription>
          </DialogHeader>
          
          {selectedUpload && (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {selectedUpload.uploads.map((upload) => (
                <Card key={upload.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <h3 className="font-semibold text-lg">{upload.type}</h3>
                        </div>
                        
                        <div className="space-y-1 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>Uploaded: {formatDate(upload.uploadedAt)}</span>
                          </div>
                          {upload.expiryDate && (
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>Expires: {formatDate(upload.expiryDate)}</span>
                              {new Date(upload.expiryDate) < new Date() && (
                                <Badge variant="destructive" className="text-xs">Expired</Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleDownloadSingle(upload)}
                          className="gap-2"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteDocument(upload)}
                          disabled={deleting}
                          className="gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          <DialogFooter className="flex justify-between">
            <Button
              variant="destructive"
              onClick={() => handleDeleteWorkerAndFiles(selectedUpload)}
              disabled={deletingWorker}
              className="gap-2"
            >
              <Trash2 className="w-4 h-4" />
              {deletingWorker ? 'Deleting...' : 'Delete Worker & All Files'}
            </Button>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminNewHires;
